import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Row, Col, Form, Button, InputGroup } from "react-bootstrap";
import { toast } from "react-toastify";
import { numericRegex } from "Utils/regex";
import {
  CURRENT_YEAR,
  EXPERIENCE_LEVELS,
  JOB_TYPES,
  MAX_SALARY,
  MIN_SALARY,
  MIN_YEAR,
  MIN_COMPANY_SIZE,
  MAX_COMPANY_SIZE,
} from "Constant/constants";
import { disablePastDate, formatWithCommas, removeCommas } from "Utils";
import { Validator } from "Utils/validator";
import {
  useGlobalSkillsList,
  useJobsPopularCategoryList,
  useOrganizationList,
} from "Constant/apiHooks";
import { newAuthService } from "Service/Axios";
import { getOrgJobDetailsUrl, saveOrgJobUrl } from "Constant/api";

const SOCIAL_MAP = [
  { key: "socialFacebook", labelKey: "Facebook" },
  { key: "socialInstagram", labelKey: "Instagram" },
  { key: "socialLinkedin", labelKey: "LinkedIn" },
  { key: "socialX", labelKey: "X" },
];

export const JobSettings = ({ formDetails }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const defaultFormData = {
    careerPageAvailable: false,
    companyName: "",
    companyDescription: "",
    companyFounded: "",
    companyOrganizationType: "",
    companySizeStart: "",
    companySizeEnd: "",
    companyPhone: "",
    companyEmail: "",
    companyWebsite: "",
    socialFacebook: "",
    socialInstagram: "",
    socialLinkedin: "",
    socialX: "",
    programName: "",
    popularCategories: [],
    skills: [],
    jobPosted: "",
    jobExpire: "",
    education: "",
    salaryStart: "",
    salaryEnd: "",
    location: "",
    jobType: "",
    experience: "",
  };

  const [formData, setFormData] = useState(defaultFormData);
  const [errMsg, setErrMsg] = useState({
    companyName: "",
    companyOrganizationType: "",
    companyFounded: "",
    companyEmail: "",
    companyWebsite: "",
    companySize: "",
    companySizeStart: "",
    companySizeEnd: "",
    socialFacebook: "",
    socialInstagram: "",
    socialLinkedin: "",
    socialX: "",
  });

  const [showCompanyNameDropdown, setShowCompanyNameDropdown] = useState(false);
  const [filteredCompanyNames, setFilteredCompanyNames] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [showCategoriesDropdown, setShowCategoriesDropdown] = useState(false);
  const [newSkill, setNewSkill] = useState("");
  const [filteredSkills, setFilteredSkills] = useState([]);
  const [showSkillsDropdown, setShowSkillsDropdown] = useState(false);

  const { organizationList, loading: organizationListLoading } =
    useOrganizationList();
  const { jobsPopularCategoryList, loading: jobsPopularCategoryListLoading } =
    useJobsPopularCategoryList();
  const { globalSkillsList, loading: globalSkillsListLoading } =
    useGlobalSkillsList();

  const parseCompanyData = (data, isFullFormDetails = true) => {
    if (!data) return {};

    let salaryStart = "";
    let salaryEnd = "";
    let companySizeStart = "";
    let companySizeEnd = "";

    if (isFullFormDetails) {
      // Parse from formDetails.jobOverview.salary
      const salaryRange = data.jobOverview?.salary || "";
      [salaryStart, salaryEnd] = salaryRange.split("-").map((s) => s.trim());

      // Parse from formDetails.companyDetails.companySize
      const companySizeRange = data.companyDetails?.companySize || "";
      [companySizeStart, companySizeEnd] = companySizeRange
        .split("-")
        .map((s) => s.trim());

      return {
        careerPageAvailable: !!data.careerPageAvailable,
        companyName: data.companyDetails?.name || "",
        companyDescription: data.companyDetails?.description || "",
        companyFounded: data.companyDetails?.founded || "",
        companyOrganizationType: data.companyDetails?.organizationType || "",
        companySizeStart: companySizeStart || "",
        companySizeEnd: companySizeEnd || "",
        companyPhone: data.companyDetails?.phone || "",
        companyEmail: data.companyDetails?.email || "",
        companyWebsite: data.companyDetails?.website || "",
        socialFacebook: data.companyDetails?.socialLinks?.facebook || "",
        socialInstagram: data.companyDetails?.socialLinks?.instagram || "",
        socialLinkedin: data.companyDetails?.socialLinks?.linkedin || "",
        socialX: data.companyDetails?.socialLinks?.x || "",
        programName: data.programName || "",
        popularCategories: Array.isArray(data.popularCategories)
          ? data.popularCategories
          : [],
        skills: Array.isArray(data.skils)
          ? data.skils
          : Array.isArray(data.skills)
          ? data.skills
          : [],
        jobPosted: data.jobOverview?.jobPosted || "",
        jobExpire: data.jobOverview?.jobExpire || "",
        education: data.jobOverview?.education || "",
        salaryStart: salaryStart || "",
        salaryEnd: salaryEnd || "",
        location: data.jobOverview?.location || "",
        jobType: data.jobOverview?.jobType || "",
        experience: data.jobOverview?.experience || "",
      };
    } else {
      // Parse from organization/API data
      const companySizeRange = data.companySize || "";
      [companySizeStart, companySizeEnd] = companySizeRange
        .split("-")
        .map((s) => s.trim());

      return {
        companyName: data.name || "",
        companyDescription: data.description || "",
        companyFounded: data.founded || "",
        companyOrganizationType: data.organizationType || "",
        companySizeStart: companySizeStart || "",
        companySizeEnd: companySizeEnd || "",
        companyPhone: data.phone || "",
        companyEmail: data.email || "",
        companyWebsite: data.website || "",
        socialFacebook: data.socialLinks?.facebook || "",
        socialInstagram: data.socialLinks?.instagram || "",
        socialLinkedin: data.socialLinks?.linkedin || "",
        socialX: data.socialLinks?.x || "",
      };
    }
  };

  const fetchOrganizationDetails = async () => {
    const payload = { formId: formDetails.id };
    const response = await newAuthService(getOrgJobDetailsUrl, "POST", payload);
    if (response.status) {
      const parsedData = parseCompanyData(response.data, true);
      setFormData((prev) => ({
        ...prev,
        ...parsedData,
      }));
    } else {
      setFormData(defaultFormData);
    }
  };

  useEffect(() => {
    if (!formDetails) return;

    fetchOrganizationDetails();
  }, [formDetails]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "companyName") {
      if (!Validator.text(value)) {
        setFilteredCompanyNames([]);
        setShowCompanyNameDropdown(false);
        setErrMsg((prev) => ({
          ...prev,
          companyName: t("common.messages.error.jobSettings.companyName.blank"),
        }));
      } else {
        const filtered = organizationList.filter((cat) =>
          cat.name.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredCompanyNames(filtered);
        setShowCompanyNameDropdown(true);
        setErrMsg((prev) => ({
          ...prev,
          companyName: "",
        }));
      }
    }

    if (name === "companyOrganizationType") {
      if (!Validator.text(value)) {
        setErrMsg((prev) => ({
          ...prev,
          companyOrganizationType: t(
            "common.messages.error.jobSettings.companyOrganizationType.blank"
          ),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          companyOrganizationType: "",
        }));
      }
    }

    if (name === "companyEmail") {
      if (Validator.text(value) && !Validator.email(value)) {
        setErrMsg((prev) => ({
          ...prev,
          companyEmail: t("common.messages.error.common.email.inValid"),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          companyEmail: "",
        }));
      }
    }

    if (
      name === "companyWebsite" ||
      name === "socialFacebook" ||
      name === "socialInstagram" ||
      name === "socialLinkedin" ||
      name === "socialX"
    ) {
      if (Validator.text(value) && !Validator.isHttpsWwwUrl(value)) {
        setErrMsg((prev) => ({
          ...prev,
          [name]: t("common.messages.error.common.httpsWwwUrl.invalid"),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          [name]: "",
        }));
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFoundedChange = (e) => {
    const value = e.target.value;

    if (!numericRegex.test(value)) return;

    setFormData((prev) => ({ ...prev, companyFounded: value }));

    if (
      Validator.text(value) &&
      !Validator.number(Number(value), MIN_YEAR, CURRENT_YEAR)
    ) {
      setErrMsg((prev) => ({
        ...prev,
        companyFounded: t(
          "common.messages.error.jobSettings.companyFounded.blank",
          {
            minYead: MIN_YEAR,
            maxYear: CURRENT_YEAR,
          }
        ),
      }));
    } else {
      setErrMsg((prev) => ({
        ...prev,
        companyFounded: "",
      }));
    }
  };

  const handleSalaryChange = (e) => {
    const { name, value } = e.target;

    const rawValue = removeCommas(value);
    const num = Number(rawValue);
    const start = Number(removeCommas(formData.salaryStart) || 0);
    const end = Number(removeCommas(formData.salaryEnd) || 0);

    let errors = {};

    if (!numericRegex.test(rawValue)) return;

    setFormData((prev) => ({
      ...prev,
      [name]: formatWithCommas(value),
    }));

    if (!Validator.number(num, MIN_SALARY, MAX_SALARY)) {
      errors[name] = t("common.messages.error.jobSettings.salary.invalid", {
        minSalary: MIN_SALARY,
        maxSalary: formatWithCommas(MAX_SALARY.toString()),
      });
    } else if (num && (num < MIN_SALARY || num > MAX_SALARY)) {
      errors[name] = t("common.messages.error.jobSettings.salary.invalid", {
        minSalary: MIN_SALARY,
        maxSalary: formatWithCommas(MAX_SALARY.toString()),
      });
    } else if (
      name === "salaryStart" &&
      end &&
      !Validator.rangeComparison(num, end)
    ) {
      errors.salaryStart = t(
        "common.messages.error.jobSettings.salaryStart.invalid"
      );
    } else if (
      name === "salaryEnd" &&
      start &&
      !Validator.rangeComparison(start, num)
    ) {
      errors.salaryEnd = t(
        "common.messages.error.jobSettings.salaryEnd.invalid"
      );
    } else {
      errors.salaryStart = "";
      errors.salaryEnd = "";
    }

    setErrMsg((prev) => ({ ...prev, ...errors }));
  };

  const handleCompanySizeChange = (e) => {
    const { name, value } = e.target;

    const num = Number(value);
    const start = Number(formData.companySizeStart || 0);
    const end = Number(formData.companySizeEnd || 0);

    let errors = {};

    if (!numericRegex.test(value)) return;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (!Validator.number(num, MIN_COMPANY_SIZE, MAX_COMPANY_SIZE)) {
      errors[name] = t(
        "common.messages.error.jobSettings.companySize.invalid",
        {
          minCompanySize: MIN_COMPANY_SIZE,
          maxCompanySize: MAX_COMPANY_SIZE,
        }
      );
    } else if (num && (num < MIN_COMPANY_SIZE || num > MAX_COMPANY_SIZE)) {
      errors[name] = t(
        "common.messages.error.jobSettings.companySize.invalid",
        {
          minCompanySize: MIN_COMPANY_SIZE,
          maxCompanySize: MAX_COMPANY_SIZE,
        }
      );
    } else if (
      name === "companySizeStart" &&
      end &&
      !Validator.rangeComparison(num, end)
    ) {
      errors.companySizeStart = t(
        "common.messages.error.jobSettings.companySizeStart.invalid"
      );
    } else if (
      name === "companySizeEnd" &&
      start &&
      !Validator.rangeComparison(start, num)
    ) {
      errors.companySizeEnd = t(
        "common.messages.error.jobSettings.companySizeEnd.invalid"
      );
    } else {
      errors.companySizeStart = "";
      errors.companySizeEnd = "";
    }

    setErrMsg((prev) => ({ ...prev, ...errors }));
  };

  // add / remove popular category
  const handleAddCategory = (categoryObj) => {
    let categoryToAdd;

    if (typeof categoryObj === "object" && categoryObj !== null) {
      // Selected from dropdown list - has id and name
      categoryToAdd = {
        gid: categoryObj.gid || null,
        name: categoryObj.title,
      };
    } else {
      // New category from input - id is null
      const categoryName = newCategory.trim();
      if (!categoryName) {
        toast.error(t("common.messages.error.jobSettings.categoryName.blank"));
        return;
      }

      // Check if category already exists
      if (
        formData.popularCategories.some(
          (c) => c.name.toLowerCase() === categoryName.toLowerCase()
        )
      ) {
        toast.error(
          t("common.messages.error.jobSettings.categoryAlreadyAdded.blank")
        );
        return;
      }

      categoryToAdd = {
        gid: null,
        name: categoryName,
      };
    }

    // Check if category already exists (by name)
    if (
      formData.popularCategories.some(
        (c) => c.name.toLowerCase() === categoryToAdd.name.toLowerCase()
      )
    ) {
      toast.error(
        t("common.messages.error.jobSettings.categoryAlreadyAdded.blank")
      );
      return;
    }

    setFormData((prev) => ({
      ...prev,
      popularCategories: [...prev.popularCategories, categoryToAdd],
    }));
    setNewCategory("");
    setShowCategoriesDropdown(false);
    setFilteredCategories([]);
  };

  const handleRemoveCategory = (index) => {
    setFormData((prev) => ({
      ...prev,
      popularCategories: prev.popularCategories.filter((_, i) => i !== index),
    }));
  };

  const handleCategoryInputChange = (e) => {
    const value = e.target.value;
    setNewCategory(value);

    if (value.trim().length > 0) {
      const filtered = jobsPopularCategoryList.filter((cat) =>
        cat.title.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredCategories(filtered);
      setShowCategoriesDropdown(true);
    } else {
      setFilteredCategories([]);
      setShowCategoriesDropdown(false);
    }
  };

  // add / remove skill
  const handleAddSkill = (skillObj) => {
    let skillToAdd;

    if (typeof skillObj === "object" && skillObj !== null) {
      // Selected from dropdown list - has gid and name
      skillToAdd = {
        gid: skillObj.id || skillObj.gid || null,
        name: skillObj.name,
      };
    } else {
      // New skill from input - gid is null
      const skillName = newSkill.trim();
      if (!skillName) {
        toast.error(t("common.messages.error.jobSettings.skillName.blank"));
        return;
      }

      // Check if skill already exists
      if (
        formData.skills.some(
          (s) => s.name.toLowerCase() === skillName.toLowerCase()
        )
      ) {
        toast.error(
          t("common.messages.error.jobSettings.skillAlreadyAdded.blank")
        );
        return;
      }

      skillToAdd = {
        gid: null,
        name: skillName,
      };
    }

    // Check if skill already exists (by name)
    if (
      formData.skills.some(
        (s) => s.name.toLowerCase() === skillToAdd.name.toLowerCase()
      )
    ) {
      toast.error(
        t("common.messages.error.jobSettings.skillAlreadyAdded.blank")
      );
      return;
    }

    setFormData((prev) => ({ ...prev, skills: [...prev.skills, skillToAdd] }));
    setNewSkill("");
    setShowSkillsDropdown(false);
    setFilteredSkills([]);
  };

  const handleRemoveSkill = (index) => {
    setFormData((prev) => ({
      ...prev,
      skills: prev.skills.filter((_, i) => i !== index),
    }));
  };

  const handleSkillInputChange = (e) => {
    const value = e.target.value;
    setNewSkill(value);

    if (value.trim().length > 0) {
      const filtered = globalSkillsList.filter((skill) =>
        skill.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredSkills(filtered);
      setShowSkillsDropdown(true);
    } else {
      setFilteredSkills([]);
      setShowSkillsDropdown(false);
    }
  };

  const handleLoadCompanyDetails = (org) => {
    if (!org) return;

    const parsedData = parseCompanyData(org, false);

    setFormData((prev) => ({
      ...prev,
      ...parsedData,
    }));

    // Close the dropdown and clear filter
    setShowCompanyNameDropdown(false);
    setFilteredCompanyNames([]);
  };

  const getValidation = async () => {
    return new Promise((resolve) => {
      let isValid = true;
      let errors = {};
      // Company Name
      if (!Validator.text(formData.companyName)) {
        isValid = false;
        errors.companyName = t(
          "common.messages.error.jobSettings.companyName.blank"
        );
      }

      // Company Organization Type
      if (!Validator.text(formData.companyOrganizationType)) {
        isValid = false;
        errors.companyOrganizationType = t(
          "common.messages.error.jobSettings.companyOrganizationType.blank"
        );
      }

      // Company Founded
      if (
        Validator.text(formData.companyFounded) &&
        !Validator.number(
          Number(formData.companyFounded),
          MIN_YEAR,
          CURRENT_YEAR
        )
      ) {
        isValid = false;
        errors.companyFounded = t(
          "common.messages.error.jobSettings.companyFounded.blank",
          {
            minYead: MIN_YEAR,
            maxYear: CURRENT_YEAR,
          }
        );
      }
      // Company Email
      if (
        Validator.text(formData.companyEmail) &&
        !Validator.email(formData.companyEmail)
      ) {
        isValid = false;
        errors.companyEmail = t("common.messages.error.common.email.inValid");
      }

      // Company Website
      if (
        Validator.text(formData.companyWebsite) &&
        !Validator.isHttpsWwwUrl(formData.companyWebsite)
      ) {
        isValid = false;
        errors.companyWebsite = t(
          "common.messages.error.common.httpsWwwUrl.invalid"
        );
      }

      // Social Links
      SOCIAL_MAP.forEach((s) => {
        if (
          Validator.text(formData[s.key]) &&
          !Validator.isHttpsWwwUrl(formData[s.key])
        ) {
          isValid = false;
          errors[s.key] = t("common.messages.error.common.httpsWwwUrl.invalid");
        }
      });

      setErrMsg((prev) => ({ ...prev, ...errors }));
      resolve(isValid);
    });
  };

  const preparePayload = () => {
    return {
      careerPageAvailable: !!formData.careerPageAvailable,
      formId: formDetails.id,
      companyDetails: {
        name: formData.companyName,
        description: formData.companyDescription,
        founded: formData.companyFounded,
        organizationType: formData.companyOrganizationType,
        companySize: `${formData.companySizeStart}-${formData.companySizeEnd}`,
        phone: formData.companyPhone,
        email: formData.companyEmail,
        website: formData.companyWebsite,
        socialLinks: {
          facebook: formData.socialFacebook,
          instagram: formData.socialInstagram,
          linkedin: formData.socialLinkedin,
          x: formData.socialX,
        },
      },

      programName: formData.programName || "",

      popularCategories: formData.popularCategories || [],

      skils: formData.skills || [],

      jobOverview: {
        jobPosted: formData.jobPosted,
        jobExpire: formData.jobExpire,
        education: formData.education,
        salary: `${formData.salaryStart}-${formData.salaryEnd}`,
        location: formData.location,
        jobType: formData.jobType,
        experience: formData.experience,
      },
    };
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const validationStatus = await getValidation();
    if (!validationStatus) {
      return;
    }
    setLoading(true);
    try {
      const payload = preparePayload();
      const response = await newAuthService(saveOrgJobUrl, "POST", payload);
      if (response.status) {
        toast.success(
          t("common.messages.notification.success.saveJobSettings")
        );
      } else {
        toast.error(response.message);
      }
    } catch {
      toast.error(t("common.messages.notification.failed.somethingWentWrong"));
    } finally {
      setLoading(false);
    }
  };

  //console.log("filterCompanyName", filteredCompanyNames);

  return (
    <Form onSubmit={handleSubmit} className="p-3 rounded-2 bg-white border">
      {/* Career page switch */}
      <Form.Check
        className="form-switch-success mb-3"
        type="switch"
        id="careerPageAvailable"
        name="careerPageAvailable"
        label={t("common.label.careerPageAvailable")}
        checked={formData.careerPageAvailable}
        onChange={handleChange}
      />

      {/* Company Details */}
      <div className="form-setting-row bg-light border p-3 rounded-2 mb-4">
        <h5>Company Details</h5>
        <hr />
        <Row>
          <Col lg={6}>
            <div className="position-relative">
              <Form.Group className="mb-3">
                <Form.Label>
                  {t("common.label.organizationName")}{" "}
                  <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder={t("common.placeHolder.organizationName")}
                  required
                />
                {errMsg.companyName && (
                  <Form.Text className="text-danger">
                    {errMsg.companyName}
                  </Form.Text>
                )}
              </Form.Group>

              {/* Dropdown for filtered categories */}
              {showCompanyNameDropdown && filteredCompanyNames.length > 0 && (
                <ul
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "white",
                    border: "1px solid #ccc",
                    borderTop: "none",
                    listStyle: "none",
                    padding: "8px 0",
                    margin: 0,
                    maxHeight: "200px",
                    overflowY: "auto",
                    zIndex: 1000,
                  }}
                >
                  {organizationListLoading ? (
                    <li style={{ padding: "8px 12px" }}>Loading...</li>
                  ) : (
                    filteredCompanyNames.map((org, idx) => (
                      <li
                        key={`${org.gid}-${idx}`}
                        onClick={() => handleLoadCompanyDetails(org)}
                        style={{
                          padding: "8px 12px",
                          cursor: "pointer",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#f5f5f5";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        {org.name}
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
          </Col>

          <Col lg={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                {t("common.label.organizationType")}{" "}
                <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                name="companyOrganizationType"
                value={formData.companyOrganizationType}
                onChange={handleChange}
                placeholder={t("common.placeHolder.organizationType")}
              />
              {errMsg.companyOrganizationType && (
                <Form.Text className="text-danger">
                  {errMsg.companyOrganizationType}
                </Form.Text>
              )}
            </Form.Group>
          </Col>

          <Col lg={12}>
            <Form.Group className="mb-3">
              <Form.Label>{t("common.label.description")}</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="companyDescription"
                value={formData.companyDescription}
                onChange={handleChange}
                placeholder={t("common.placeHolder.description")}
              />
            </Form.Group>
          </Col>

          <Col lg={4}>
            <Form.Group className="mb-3">
              <Form.Label>{t("common.label.companySize")}</Form.Label>
              <div className="d-flex gap-2">
                <div className="">
                  <InputGroup>
                    <Form.Control
                      name="companySizeStart"
                      value={formData.companySizeStart}
                      onChange={handleCompanySizeChange}
                      placeholder={t("common.placeHolder.companySizeStart")}
                      maxLength={8}
                    />
                  </InputGroup>
                  {errMsg.companySizeStart && (
                    <Form.Text className="text-danger">
                      {errMsg.companySizeStart}
                    </Form.Text>
                  )}
                </div>
                <div className="">
                  <span className="d-block text-center">-</span>
                </div>
                <div className="">
                  <InputGroup>
                    <Form.Control
                      name="companySizeEnd"
                      value={formData.companySizeEnd}
                      onChange={handleCompanySizeChange}
                      placeholder={t("common.placeHolder.companySizeEnd")}
                      maxLength={8}
                    />
                  </InputGroup>
                  {errMsg.companySizeEnd && (
                    <Form.Text className="text-danger">
                      {errMsg.companySizeEnd}
                    </Form.Text>
                  )}
                </div>
              </div>
            </Form.Group>
          </Col>

          <Col lg={4}>
            <Form.Group className="mb-3">
              <Form.Label>{t("common.label.founded")}</Form.Label>
              <Form.Control
                maxLength={4}
                name="companyFounded"
                value={formData.companyFounded}
                onChange={handleFoundedChange}
                placeholder={t("common.placeHolder.founded")}
              />
              {errMsg.companyFounded && (
                <Form.Text className="text-danger">
                  {errMsg.companyFounded}
                </Form.Text>
              )}
            </Form.Group>
          </Col>

          <Col lg={4}>
            <Form.Group className="mb-3">
              <Form.Label>{t("common.label.website")}</Form.Label>
              <Form.Control
                name="companyWebsite"
                value={formData.companyWebsite}
                onChange={handleChange}
                placeholder={t("common.placeHolder.website")}
              />
              {errMsg.companyWebsite && (
                <Form.Text className="text-danger">
                  {errMsg.companyWebsite}
                </Form.Text>
              )}
            </Form.Group>
          </Col>

          <Col lg={6}>
            <Form.Group className="mb-3">
              <Form.Label>{t("common.label.email")}</Form.Label>
              <Form.Control
                name="companyEmail"
                value={formData.companyEmail}
                onChange={handleChange}
                placeholder={t("common.placeHolder.email")}
              />
              {errMsg.companyEmail && (
                <Form.Text className="text-danger">
                  {errMsg.companyEmail}
                </Form.Text>
              )}
            </Form.Group>
          </Col>

          <Col lg={6}>
            <Form.Group className="mb-3">
              <Form.Label>{t("common.label.phone")}</Form.Label>
              <Form.Control
                name="companyPhone"
                value={formData.companyPhone}
                onChange={handleChange}
                placeholder={t("common.placeHolder.phone")}
              />
            </Form.Group>
          </Col>

          {/* Social Links */}
          <Col lg={12}>
            <h6 className="mt-3">{t("common.label.socialLinks")}</h6>
            <hr />
          </Col>

          {SOCIAL_MAP.map((s) => (
            <Col lg={6} md={6} key={s.key}>
              <Form.Group className="mb-3">
                <Form.Label>{s.labelKey}</Form.Label>
                <Form.Control
                  name={s.key}
                  value={formData[s.key]}
                  onChange={handleChange}
                  placeholder={`e.g., https://www.${s.labelKey.toLowerCase()}.com`}
                />
                {errMsg[s.key] && (
                  <Form.Text className="text-danger">{errMsg[s.key]}</Form.Text>
                )}
              </Form.Group>
            </Col>
          ))}
        </Row>
      </div>

      {/* Program Name */}
      <div className="form-setting-row bg-light border p-3 rounded-2 mb-4">
        <Form.Group>
          <Form.Label>{t("common.label.programName")}</Form.Label>
          <Form.Control
            name="programName"
            value={formData.programName}
            onChange={handleChange}
            placeholder={t("common.placeHolder.programName")}
          />
        </Form.Group>
      </div>

      {/* Popular Categories */}
      <div className="form-setting-row bg-light border p-3 rounded-2 mb-4">
        <h5>{t("common.label.popularCategories")}</h5>
        <Row className="align-items-center">
          <Col lg={12}>
            <div className="position-relative">
              <InputGroup>
                <Form.Control
                  placeholder={t("common.placeHolder.seaerchOrAddCategoryName")}
                  value={newCategory}
                  onChange={handleCategoryInputChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddCategory();
                    }
                  }}
                  onFocus={() => {
                    if (newCategory.trim().length > 0) {
                      setShowCategoriesDropdown(true);
                    }
                  }}
                />
                <Button
                  onClick={() => handleAddCategory()}
                  disabled={!newCategory.trim()}
                >
                  {t("common.button.addNew")}
                </Button>
              </InputGroup>

              {/* Dropdown for filtered categories */}
              {showCategoriesDropdown && filteredCategories.length > 0 && (
                <ul
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "white",
                    border: "1px solid #ccc",
                    borderTop: "none",
                    listStyle: "none",
                    padding: "8px 0",
                    margin: 0,
                    maxHeight: "200px",
                    overflowY: "auto",
                    zIndex: 1000,
                  }}
                >
                  {jobsPopularCategoryListLoading ? (
                    <li style={{ padding: "8px 12px" }}>Loading...</li>
                  ) : (
                    filteredCategories.map((cat, idx) => (
                      <li
                        key={`${cat.gid}-${idx}`}
                        onClick={() => handleAddCategory(cat)}
                        style={{
                          padding: "8px 12px",
                          cursor: "pointer",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#f5f5f5";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        {cat.title}
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
          </Col>
        </Row>

        <Row className="mt-3">
          {formData.popularCategories.map((cat, idx) => (
            <Col key={idx} lg={3} className="mb-2">
              <div className="p-2 border rounded d-flex justify-content-between align-items-center">
                <div>
                  <div style={{ fontSize: 14 }}>{cat.name}</div>
                  {cat.id && (
                    <div style={{ fontSize: 11, color: "#666" }}>
                      ID: {cat.id}
                    </div>
                  )}
                </div>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleRemoveCategory(idx)}
                >
                  x
                </Button>
              </div>
            </Col>
          ))}
        </Row>
      </div>

      {/* Skills */}
      <div className="form-setting-row bg-light border p-3 rounded-2 mb-4">
        <h5>{t("common.label.skills")}</h5>
        <Row className="align-items-center">
          <Col lg={12}>
            <div className="position-relative">
              <InputGroup>
                <Form.Control
                  placeholder={t("common.placeHolder.searchOrAddSkills")}
                  value={newSkill}
                  onChange={handleSkillInputChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddSkill();
                    }
                  }}
                  onFocus={() => {
                    if (newSkill.trim().length > 0) {
                      setShowSkillsDropdown(true);
                    }
                  }}
                />
                <Button
                  onClick={() => handleAddSkill()}
                  disabled={!newSkill.trim()}
                >
                  {t("common.button.addNew")}
                </Button>
              </InputGroup>

              {/* Dropdown for filtered skills */}
              {showSkillsDropdown && filteredSkills.length > 0 && (
                <ul
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "white",
                    border: "1px solid #ccc",
                    borderTop: "none",
                    listStyle: "none",
                    padding: "8px 0",
                    margin: 0,
                    maxHeight: "200px",
                    overflowY: "auto",
                    zIndex: 1000,
                  }}
                >
                  {globalSkillsListLoading ? (
                    <li>Loading...</li>
                  ) : (
                    filteredSkills.map((skill, idx) => (
                      <li
                        key={`${skill.gid}-${idx}`}
                        onClick={() => handleAddSkill(skill)}
                        style={{
                          padding: "8px 12px",
                          cursor: "pointer",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#f5f5f5";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        {skill.name}
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
          </Col>
        </Row>

        <Row className="mt-3">
          {formData.skills.map((s, idx) => (
            <Col key={idx} lg={3} className="mb-2">
              <div className="p-2 border rounded d-flex justify-content-between align-items-center">
                <div>
                  <div style={{ fontSize: 14 }}>{s.name}</div>
                </div>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleRemoveSkill(idx)}
                >
                  x
                </Button>
              </div>
            </Col>
          ))}
        </Row>
      </div>

      {/* Job Overview (explicit fields) */}
      <div className="form-setting-row bg-light border p-3 rounded-2 mb-4">
        <h5>{t("common.label.jobOverview")}</h5>
        <hr />
        <Row>
          <Col lg={6}>
            <Form.Group className="mb-3">
              <Form.Label>{t("common.label.jobPosted")}</Form.Label>
              <Form.Control
                type="date"
                name="jobPosted"
                value={formData.jobPosted}
                onChange={handleChange}
              />
            </Form.Group>
          </Col>

          <Col lg={6}>
            <Form.Group className="mb-3">
              <Form.Label>{t("common.label.jobExpire")}</Form.Label>
              <Form.Control
                type="date"
                name="jobExpire"
                value={formData.jobExpire}
                onChange={handleChange}
                min={disablePastDate(formData.jobPosted)}
              />
            </Form.Group>
          </Col>

          <Col lg={6}>
            <Form.Group className="mb-3">
              <Form.Label>{t("common.label.educationLevel")}</Form.Label>
              <Form.Control
                name="education"
                value={formData.education}
                onChange={handleChange}
                placeholder={t("common.placeHolder.educationLevel")}
              />
            </Form.Group>
          </Col>
          <p>{MAX_SALARY.length}</p>
          <Col lg={6}>
            <Form.Group className="mb-3">
              <Form.Label>{t("common.label.salary")}</Form.Label>
              <div className="d-flex gap-2">
                <div className="">
                  <InputGroup>
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      name="salaryStart"
                      value={formData.salaryStart}
                      onChange={handleSalaryChange}
                      placeholder={t("common.placeHolder.salaryStart")}
                      maxLength={8}
                    />
                  </InputGroup>
                  {errMsg.salaryStart && (
                    <Form.Text className="text-danger">
                      {errMsg.salaryStart}
                    </Form.Text>
                  )}
                </div>
                <div className="">
                  <span className="d-block text-center">-</span>
                </div>
                <div className="">
                  <InputGroup>
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      name="salaryEnd"
                      value={formData.salaryEnd}
                      onChange={handleSalaryChange}
                      placeholder={t("common.placeHolder.salaryEnd")}
                      maxLength={8}
                    />
                  </InputGroup>
                  {errMsg.salaryEnd && (
                    <Form.Text className="text-danger">
                      {errMsg.salaryEnd}
                    </Form.Text>
                  )}
                </div>
              </div>
            </Form.Group>
          </Col>

          <Col lg={6}>
            <Form.Group className="mb-3">
              <Form.Label>{t("common.label.location")}</Form.Label>
              <Form.Control
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder={t("common.placeHolder.location")}
              />
            </Form.Group>
          </Col>

          <Col lg={6}>
            <Form.Group className="mb-3">
              <Form.Label>{t("common.label.jobType")}</Form.Label>
              <Form.Select
                name="jobType"
                value={formData.jobType}
                onChange={handleChange}
              >
                <option value="">{t("common.placeHolder.jobType")}</option>
                {JOB_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Form.Select>
              {errMsg.jobType && (
                <Form.Text className="text-danger">{errMsg.jobType}</Form.Text>
              )}
            </Form.Group>
          </Col>

          <Col lg={6}>
            <Form.Group className="mb-3">
              <Form.Label>{t("common.label.experience")}</Form.Label>
              <Form.Select
                name="experience"
                value={formData.experience}
                onChange={handleChange}
              >
                <option value="">{t("common.placeHolder.experience")}</option>
                {EXPERIENCE_LEVELS.map((level) => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </Form.Select>
              {errMsg.experience && (
                <Form.Text className="text-danger">
                  {errMsg.experience}
                </Form.Text>
              )}
            </Form.Group>
          </Col>
        </Row>
      </div>

      <div className="d-flex">
        <Button variant="primary" onClick={handleSubmit} disabled={loading}>
          {loading ? t("common.button.save") : t("common.button.save")}
        </Button>
      </div>
    </Form>
  );
};
